import React, { useRef, useState } from "react";
import { Alert, Button, Col, Container, Row, Card, CardBody } from "shards-react";
import PageTitle from "../components/common/PageTitle";
import { uploadReportToDrive } from "../backend/drive";

export const UploadReports = () => {
  const [files, setFiles] = useState([]);
  const [uploadStatuses, setUploadStatuses] = useState({}); // { fileName: "loading" | "success" | "error" }
  const [globalStatus, setGlobalStatus] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const inputRef = useRef();

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(selected);
    setUploadStatuses({});
    setGlobalStatus(null);
    setPreviewFile(selected[0] || null);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setGlobalStatus("loading");

    const results = await Promise.allSettled(
      files.map(async (file) => {
        setUploadStatuses((prev) => ({ ...prev, [file.name]: "loading" }));
        try {
          await uploadReportToDrive(file);
          setUploadStatuses((prev) => ({ ...prev, [file.name]: "success" }));
        } catch (e) {
          console.error(e);
          setUploadStatuses((prev) => ({ ...prev, [file.name]: "error" }));
          throw e;
        }
      })
    );

    const anyError = results.some((r) => r.status === "rejected");
    const anySuccess = results.some((r) => r.status === "fulfilled");

    if (anyError && anySuccess) setGlobalStatus("partial");
    else if (anyError) setGlobalStatus("error");
    else {
      setGlobalStatus("success");
      setFiles([]);
      setPreviewFile(null);
      inputRef.current.value = "";
    }

    setTimeout(() => setGlobalStatus(null), 6000);
  };

  const handleRemoveFile = (fileName) => {
    const updated = files.filter((f) => f.name !== fileName);
    setFiles(updated);
    if (previewFile?.name === fileName) {
      setPreviewFile(updated[0] || null);
    }
    setUploadStatuses((prev) => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    });
  };

  const isLoading = globalStatus === "loading";
  const previewUrl = previewFile ? URL.createObjectURL(previewFile) : null;

  return (
    <Container fluid className="main-content-container pb-4">
      <PageTitle title="Subir Informes" md="12" className="ml-sm-auto mr-sm-auto" />

      <Row>
        {/* LEFT PANEL */}
        <Col md="6">
          <Card className="mb-4 shadow-sm">
            <CardBody>
              <h5 className="mb-3">Seleccionar archivos</h5>

              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.xlsx,.xls,.doc,.docx"
                onChange={handleFileChange}
                className="form-control mb-3"
                multiple
              />

              {/* File list */}
              {files.length > 0 && (
                <ul className="list-group mb-3">
                  {files.map((file) => {
                    const st = uploadStatuses[file.name];
                    return (
                      <li
                        key={file.name}
                        className="list-group-item d-flex justify-content-between align-items-center px-2 py-2"
                        style={{
                          cursor: "pointer",
                          background: previewFile?.name === file.name ? "#f0f4ff" : "white",
                        }}
                        onClick={() => setPreviewFile(file)}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span
                            className="d-block text-truncate"
                            style={{ maxWidth: "180px", fontWeight: 500 }}
                            title={file.name}
                          >
                            {file.name}
                          </span>
                          <small className="text-muted">{(file.size / 1024).toFixed(1)} KB</small>
                        </div>

                        <div className="d-flex align-items-center">
                          {st === "loading" && <span className="badge badge-secondary mr-2">Subiendo…</span>}
                          {st === "success" && <span className="badge badge-success mr-2">✓ Subido</span>}
                          {st === "error"   && <span className="badge badge-danger mr-2">✗ Error</span>}

                          {!isLoading && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => { e.stopPropagation(); handleRemoveFile(file.name); }}
                              title="Eliminar"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <Button
                theme="primary"
                block
                disabled={!files.length || isLoading}
                onClick={handleUpload}
              >
                {isLoading
                  ? "Subiendo..."
                  : `Subir ${files.length > 1 ? `${files.length} archivos` : "archivo"} a Drive`}
              </Button>

              {globalStatus === "success" && (
                <Alert className="mt-3" theme="success">Todos los informes se subieron correctamente.</Alert>
              )}
              {globalStatus === "partial" && (
                <Alert className="mt-3" theme="warning">Algunos archivos no pudieron subirse. Revisa los errores.</Alert>
              )}
              {globalStatus === "error" && (
                <Alert className="mt-3" theme="danger">Error al subir los informes. Comprueba tu sesión de Google.</Alert>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT PANEL - PREVIEW */}
        <Col md="6">
          <Card className="mb-4 shadow-sm" style={{ height: "100%" }}>
            <CardBody style={{ height: "500px" }} className="d-flex flex-column justify-content-center align-items-center">
              {!previewFile && (
                <span className="text-muted">Vista previa del archivo</span>
              )}

              {previewFile && (
                <small className="text-muted mb-2 w-100 text-truncate text-center" title={previewFile.name}>
                  {previewFile.name}
                </small>
              )}

              {previewUrl && previewFile?.type === "application/pdf" && (
                <iframe
                  src={previewUrl}
                  title="preview"
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              )}

              {previewFile && previewFile.type !== "application/pdf" && (
                <div className="text-center text-muted">
                  <p>No hay vista previa disponible para este tipo de archivo.</p>
                  <p>Puedes subirlo igualmente.</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UploadReports;